import React from 'react';
import { Button, Flex, LoadingOrbit, SettingsSection, TagBadge, Text } from '@chatsdet/ui';
import { CalendarBlank, Clock, GithubLogo, Robot } from '@phosphor-icons/react';
import { useReleaseConfig } from '../../../../features/releases/useReleases';
import { ConfigureReleaseDialog } from '../../../../features/releases/components/ConfigureReleaseDialog';
import type { ReleaseQAConfigSuiteEntry, ScheduleSettings } from '../../../../features/releases/releaseTypes';

type ReleaseQAConfig = {
  id: string;
  orgId: string;
  propertyId: string;
  lookbackDays: number;
  /** Resolved schedule from GET /release-qa/configs/:propertyId; null when none or paused. */
  schedule?: ScheduleSettings | null;
  agentMayPromote: boolean;
  createdAt: string;
  updatedAt: string;
};

/** One linked repo on the config (from the multi-repo junction). */
type ConfigRepo = {
  propertyGithubRepoId: string;
  releaseBranch: string | null;
  repoFullName: string;
};

function ReleaseQAConfiguredState({
  config,
  repos,
  suites,
}: {
  config: ReleaseQAConfig;
  repos: ConfigRepo[];
  suites: ReleaseQAConfigSuiteEntry[];
}) {
  // The first entry (lowest displayOrder) is the primary repo.
  const primaryRepo = repos[0] ?? null;
  const regressionScope =
    suites.length > 0
      ? suites.map((s) => s.name).join(', ')
      : 'All promoted workflows (legacy — no suites configured)';

  return (
    <Flex direction="column" gap="sm">
      <Flex align="center" gap="sm">
        <GithubLogo size={16} className="text-muted-foreground" />
        <Text size="sm">{primaryRepo?.repoFullName ?? 'Unknown repository'}</Text>
        {primaryRepo?.releaseBranch && (
          <TagBadge name={primaryRepo.releaseBranch} color="gray" hideDot className="text-xs" />
        )}
        {repos.length > 1 && (
          <Text size="xs" className="text-muted-foreground">
            +{repos.length - 1} more {repos.length - 1 === 1 ? 'repo' : 'repos'}
          </Text>
        )}
      </Flex>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
        <div>
          <Text size="xs" className="text-muted-foreground">
            Lookback
          </Text>
          <Text size="sm" weight="medium" className="tabular-nums">
            {config.lookbackDays} days
          </Text>
        </div>
        <div>
          <Text size="xs" className="text-muted-foreground">
            Suites
          </Text>
          <Text size="sm" weight="medium" className="text-pretty">
            {regressionScope}
          </Text>
        </div>
      </div>
      {config.schedule ? (
        <Flex align="center" gap="xs" className="mt-2">
          <Clock size={14} className="text-primary" />
          <Text size="sm" className="text-primary">
            Scheduled runs enabled
          </Text>
        </Flex>
      ) : (
        <Flex align="center" gap="xs" className="mt-2">
          <CalendarBlank size={14} className="text-muted-foreground" />
          <Text size="sm" className="text-muted-foreground">
            No schedule configured
          </Text>
        </Flex>
      )}
      <Flex align="center" gap="xs" className="mt-2">
        <Robot size={14} className="text-primary" />
        <Text size="sm" className="text-primary">
          Agent · {config.agentMayPromote ? 'promote on' : 'promote off'}
        </Text>
      </Flex>
    </Flex>
  );
}

function ReleaseQANotConfiguredState() {
  return (
    <Flex direction="column" align="center" gap="sm" className="py-4">
      <CalendarBlank size={32} className="text-muted-foreground" />
      <Text size="sm" className="text-muted-foreground text-center text-pretty">
        Release QA is not configured for this application.
      </Text>
      <Text size="xs" className="text-muted-foreground text-center text-pretty">
        Configure it to automatically test code changes.
      </Text>
    </Flex>
  );
}

/** Automated testing triggered by code changes. Gated on the `releaseQa` flag. */
export function ReleaseQASection({ propertyId }: { propertyId: string }) {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const releaseConfigQuery = useReleaseConfig(propertyId);

  return (
    <>
      <SettingsSection
        title="Release QA"
        description="Automated testing triggered by code changes"
        action={
          <Button variant="secondary" size="sm" onPress={() => setIsDialogOpen(true)}>
            {releaseConfigQuery.data?.config ? 'Edit Configuration' : 'Configure'}
          </Button>
        }
      >
        {releaseConfigQuery.isLoading ? (
          <Flex align="center" justify="center" className="py-4">
            <LoadingOrbit size="md" />
          </Flex>
        ) : releaseConfigQuery.data?.config ? (
          <ReleaseQAConfiguredState
            config={releaseConfigQuery.data.config}
            repos={releaseConfigQuery.data.repos ?? []}
            suites={releaseConfigQuery.data.suites ?? []}
          />
        ) : (
          <ReleaseQANotConfiguredState />
        )}
      </SettingsSection>

      <ConfigureReleaseDialog
        propertyId={propertyId}
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        existingConfig={releaseConfigQuery.data?.config}
        existingRepos={releaseConfigQuery.data?.repos}
        existingSuites={releaseConfigQuery.data?.suites}
      />
    </>
  );
}
